/**
 * Sovrapponibilità mansioni (gruppi di equivalenza): admin UI su
 * phc_hrplnmansionemap, oggi raggiungibile solo via API/drill-down in
 * Fattibilità (vedi domain/mansioniEquivalenza.js per la semantica di
 * peso_pct). Elenco raggruppato per gruppo + form guidato + avvisi
 * espliciti quando i dati non permettono un calcolo affidabile, invece
 * di lasciare che l'aggregazione produca silenziosamente un numero
 * ambiguo o zero.
 */
import React, { useMemo, useState } from "react";
import { useApi } from "../../hooks/useApi";
import { useAuth } from "../../context/AuthContext";
import {
  Card,
  Spinner,
  ErrorBox,
  SimpleTable,
  WarningList,
} from "../../components/ui/basics";
import MansioneSelect from "../../components/MansioneSelect";

function calcolaAvvisi(rows) {
  const avvisi = [];
  const pieniPerCodice = new Map();
  for (const r of rows) {
    if (r.attivo === false || r.pesoPct < 100) continue;
    if (!pieniPerCodice.has(r.codiceSorgente))
      pieniPerCodice.set(r.codiceSorgente, []);
    pieniPerCodice.get(r.codiceSorgente).push(r.gruppoEquivalenza);
  }
  for (const [codice, gruppi] of pieniPerCodice) {
    if (gruppi.length > 1) {
      avvisi.push({
        code: "codice_ambiguo",
        message: `"${codice}" è mappato al 100% su più gruppi contemporaneamente (${gruppi.join(", ")}): il calcolo userà uno a caso, in modo non deterministico. Disattiva o elimina i doppioni.`,
      });
    }
  }

  const membriPerGruppo = new Map();
  for (const r of rows) {
    if (r.attivo === false || r.pesoPct < 100) continue;
    if (!membriPerGruppo.has(r.gruppoEquivalenza))
      membriPerGruppo.set(r.gruppoEquivalenza, new Set());
    membriPerGruppo.get(r.gruppoEquivalenza).add(r.codiceSorgente);
  }
  for (const [gruppo, membri] of membriPerGruppo) {
    if (membri.size === 1 && !membri.has(gruppo)) {
      avvisi.push({
        code: "gruppo_singolo",
        message: `Il gruppo "${gruppo}" ha un solo componente ("${[...membri][0]}"): è solo un rinomina, nessuna reale sovrapposizione applicata.`,
      });
    }
  }

  return avvisi;
}

export default function GruppiEquivalenzaSection() {
  const { apiRequest } = useAuth();
  const [mostraDisattivi, setMostraDisattivi] = useState(false);
  const endpoint = `/planning/mansioni-mappature${mostraDisattivi ? "?tutte=1" : ""}`;
  const { data, loading, error, reload } = useApi(endpoint, [mostraDisattivi]);
  const { data: mansioniData } = useApi("/planning/mansioni");
  const [modal, setModal] = useState(null);

  const rows = data?.items || [];
  const gruppiEsistenti = useMemo(
    () => [...new Set(rows.map((r) => r.gruppoEquivalenza))].sort(),
    [rows],
  );
  const avvisi = useMemo(() => calcolaAvvisi(rows), [rows]);

  const rowsOrdinate = useMemo(
    () =>
      [...rows].sort(
        (a, b) =>
          a.gruppoEquivalenza.localeCompare(b.gruppoEquivalenza) ||
          a.codiceSorgente.localeCompare(b.codiceSorgente),
      ),
    [rows],
  );

  const submit = async (values) => {
    const payload = {
      ...values,
      pesoPct: Number(values.pesoPct),
    };
    if (modal.mode === "create") {
      await apiRequest("/planning/mansioni-mappature", {
        method: "POST",
        body: JSON.stringify(payload),
      });
    } else {
      await apiRequest(`/planning/mansioni-mappature/${modal.record.id}`, {
        method: "PATCH",
        body: JSON.stringify(payload),
      });
    }
    reload();
  };

  const elimina = async (r) => {
    if (
      !window.confirm(
        `Eliminare la mappatura "${r.codiceSorgente}" → "${r.gruppoEquivalenza}"?`,
      )
    )
      return;
    await apiRequest(`/planning/mansioni-mappature/${r.id}`, {
      method: "DELETE",
    });
    reload();
  };

  return (
    <Card
      title="Sovrapponibilità mansioni"
      subtitle="Mansioni che condividono il fabbisogno (pool) o si coprono parzialmente a vicenda"
      actions={
        <div className="flex gap-2 items-center">
          <label className="flex items-center gap-1.5 text-xs text-gray-500">
            <input
              type="checkbox"
              checked={mostraDisattivi}
              onChange={(e) => setMostraDisattivi(e.target.checked)}
            />
            Mostra disattive
          </label>
          <button
            onClick={() => setModal({ mode: "create" })}
            className="text-sm bg-primary-600 text-white px-3 py-1.5 rounded hover:bg-primary-700"
          >
            + Nuova mappatura
          </button>
        </div>
      }
    >
      <div className="bg-blue-50 border border-blue-200 text-blue-800 rounded-lg p-3 text-xs mb-3">
        Una mansione mappata al <strong>100%</strong> su un gruppo entra a far
        parte di un pool unico con le altre mansioni dello stesso gruppo (es.
        servizio alberghiero e reception pooled insieme). Un peso{" "}
        <strong>inferiore al 100%</strong> aggiunge invece un contributo
        parziale, limitato a quella percentuale del fabbisogno del gruppo target
        (es. OSS-S copre max il 30% delle ore infermieristiche).
      </div>

      <WarningList warnings={avvisi} />

      {loading && <Spinner />}
      {error && <ErrorBox message={error} onRetry={reload} />}
      {!loading && !error && (
        <SimpleTable
          rowKey={(r) => r.id}
          columns={[
            { key: "gruppoEquivalenza", label: "Gruppo" },
            { key: "codiceSorgente", label: "Codice sorgente" },
            {
              key: "pesoPct",
              label: "Peso",
              align: "right",
              render: (r) => (
                <span
                  className={
                    r.pesoPct >= 100
                      ? "font-medium text-gray-800"
                      : "text-blue-700"
                  }
                >
                  {r.pesoPct}%{r.pesoPct >= 100 ? " (pieno)" : " (parziale)"}
                </span>
              ),
            },
            {
              key: "attivo",
              label: "Stato",
              render: (r) =>
                r.attivo === false ? (
                  <span className="text-gray-400">Disattiva</span>
                ) : (
                  <span className="text-green-700">Attiva</span>
                ),
            },
            { key: "note", label: "Note" },
            {
              key: "azioni",
              label: "",
              render: (r) => (
                <div className="flex gap-2 justify-end">
                  <button
                    onClick={() => setModal({ mode: "edit", record: r })}
                    className="text-xs text-primary-600 hover:underline"
                  >
                    Modifica
                  </button>
                  <button
                    onClick={() => elimina(r)}
                    className="text-xs text-red-600 hover:underline"
                  >
                    Elimina
                  </button>
                </div>
              ),
            },
          ]}
          rows={rowsOrdinate}
        />
      )}

      {modal && (
        <MappaturaModal
          mode={modal.mode}
          record={modal.record}
          gruppiEsistenti={gruppiEsistenti}
          mansioni={mansioniData?.items || []}
          onSubmit={submit}
          onClose={() => setModal(null)}
        />
      )}
    </Card>
  );
}

function MappaturaModal({
  mode,
  record,
  gruppiEsistenti,
  mansioni,
  onSubmit,
  onClose,
}) {
  const [values, setValues] = useState(
    record || { pesoPct: 100, attivo: true },
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await onSubmit(values);
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-md">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <h3 className="font-semibold text-gray-800">
            {mode === "create" ? "Nuova mappatura" : "Modifica mappatura"}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        </div>
        <form onSubmit={submit} className="p-4 space-y-3">
          <label className="block text-sm">
            <span className="block text-xs text-gray-500 mb-1">
              Codice sorgente *
            </span>
            <MansioneSelect
              value={values.codiceSorgente}
              onChange={(v) => setValues((s) => ({ ...s, codiceSorgente: v }))}
              required
              className="border border-gray-300 rounded px-2 py-1.5 w-full text-sm"
            />
            <span className="block text-xs text-gray-400 mt-1">
              La mansione che vuoi far confluire in un gruppo.
            </span>
          </label>
          <label className="block text-sm">
            <span className="block text-xs text-gray-500 mb-1">
              Gruppo di equivalenza *
            </span>
            <input
              type="text"
              list="gruppi-esistenti"
              required
              value={values.gruppoEquivalenza ?? ""}
              onChange={(e) =>
                setValues((s) => ({ ...s, gruppoEquivalenza: e.target.value }))
              }
              placeholder="es. un codice mansione esistente, o un nome nuovo di gruppo"
              className="border border-gray-300 rounded px-2 py-1.5 w-full text-sm"
            />
            <datalist id="gruppi-esistenti">
              {gruppiEsistenti.map((g) => (
                <option key={g} value={g} />
              ))}
              {mansioni.map((m) => (
                <option key={m.codice} value={m.codice} />
              ))}
            </datalist>
            <span className="block text-xs text-gray-400 mt-1">
              Scegli un gruppo esistente dal suggerimento, oppure scrivi un nome
              nuovo per crearne uno.
            </span>
          </label>
          <label className="block text-sm">
            <span className="block text-xs text-gray-500 mb-1">Peso % *</span>
            <input
              type="number"
              min="1"
              max="100"
              required
              value={values.pesoPct ?? 100}
              onChange={(e) =>
                setValues((s) => ({ ...s, pesoPct: e.target.value }))
              }
              className="border border-gray-300 rounded px-2 py-1.5 w-full text-sm"
            />
            <span className="block text-xs text-gray-400 mt-1">
              100% = pool pieno con il gruppo. Meno di 100% = contributo
              parziale, capato a quella percentuale del fabbisogno del gruppo
              target.
            </span>
          </label>
          <label className="block text-sm">
            <span className="block text-xs text-gray-500 mb-1">Note</span>
            <input
              type="text"
              value={values.note ?? ""}
              onChange={(e) =>
                setValues((s) => ({ ...s, note: e.target.value }))
              }
              className="border border-gray-300 rounded px-2 py-1.5 w-full text-sm"
            />
          </label>
          {mode === "edit" && (
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={values.attivo !== false}
                onChange={(e) =>
                  setValues((s) => ({ ...s, attivo: e.target.checked }))
                }
              />
              Attiva
            </label>
          )}
          {error && <p className="text-xs text-red-600">{error}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="text-sm px-3 py-1.5 rounded border border-gray-300 text-gray-600 hover:bg-gray-50"
            >
              Annulla
            </button>
            <button
              type="submit"
              disabled={saving}
              className="bg-primary-600 text-white text-sm px-4 py-1.5 rounded hover:bg-primary-700 disabled:opacity-50"
            >
              {saving ? "Salvataggio…" : "Salva"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
